import { useEffect, useRef, useCallback } from "react";

export type VisualEffect = "gravity" | "matrix" | "disco";
export type MiniGame = "brickbreaker" | "clickamole";
export type EasterEggState =
  | { type: "none" }
  | { type: "visual"; effect: VisualEffect }
  | { type: "game"; game: MiniGame };

const KONAMI = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
];

const VISUAL_EFFECTS: VisualEffect[] = ["gravity", "matrix", "disco"];
const MINI_GAMES: MiniGame[] = ["brickbreaker", "clickamole"];

/** Fisher-Yates shuffle — returns a new shuffled copy */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Shuffled-deck picker.
 * Works through a shuffled copy of the list in order, then reshuffles.
 * Guarantees every item appears exactly once before any repeat,
 * so you'll never see the same effect twice in a row or skip an effect
 * across many triggers.
 */
function makeDeckPicker<T>(items: T[]) {
  let deck: T[] = shuffle(items);
  let idx = 0;

  return () => {
    if (idx >= deck.length) {
      // Exhaused — reshuffle, but ensure first card isn't the same as the
      // last card of the previous deck (avoids a repeat at the seam).
      const last = deck[deck.length - 1];
      do {
        deck = shuffle(items);
      } while (items.length > 1 && deck[0] === last);
      idx = 0;
    }
    return deck[idx++];
  };
}

// Module-level pickers — persist across re-renders / remounts so the
// sequence continues even if the Home page unmounts and remounts.
const pickVisualEffect = makeDeckPicker(VISUAL_EFFECTS);
const pickMiniGame = makeDeckPicker(MINI_GAMES);

interface UseEasterEggsOptions {
  onActivate: (state: EasterEggState) => void;
  onDeactivate: () => void;
  active: EasterEggState;
}

export function useEasterEggs({
  onActivate,
  onDeactivate,
  active,
}: UseEasterEggsOptions) {
  const escCountRef = useRef(0);
  const konamiBufferRef = useRef<string[]>([]);
  const lastEscTimeRef = useRef(0);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key;

      // ── Mini-game active: double-ESC within 400ms dismisses it ──
      if (active.type === "game") {
        if (key === "Escape") {
          const now = Date.now();
          if (now - lastEscTimeRef.current < 400) {
            lastEscTimeRef.current = 0;
            onDeactivate();
          } else {
            lastEscTimeRef.current = now;
          }
        }
        return;
      }

      // ── Visual effect active: ignore all key input ──
      if (active.type === "visual") return;

      // ── ESC counter (Group 1 trigger) ──
      if (key === "Escape") {
        escCountRef.current += 1;
        if (escCountRef.current >= 7) {
          escCountRef.current = 0;
          konamiBufferRef.current = [];
          onActivate({ type: "visual", effect: pickVisualEffect() });
        }
      } else {
        escCountRef.current = 0;
      }

      // ── Konami buffer (Group 2 trigger) ──
      konamiBufferRef.current = [
        ...konamiBufferRef.current,
        key,
      ].slice(-KONAMI.length);

      if (
        konamiBufferRef.current.length === KONAMI.length &&
        konamiBufferRef.current.every((k, i) => k === KONAMI[i])
      ) {
        konamiBufferRef.current = [];
        escCountRef.current = 0;
        onActivate({ type: "game", game: pickMiniGame() });
      }
    },
    [active, onActivate, onDeactivate],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);
}
